/**
 * Отчёт о промахе: подсказка сломалась, и мы узнаём об этом сами.
 *
 * Возить селекторы с сервера без обратной связи — половина дела и худшая:
 * править-то можно быстро, а узнавать о поломке мы по-прежнему будем от
 * человека, который в эту секунду теряет вилку. Расширение же видит промах
 * прямо на месте: число на странице есть, а ячейка не нашлась или человек нажал
 * не туда, куда мы показывали.
 *
 * Что уезжает: код площадки, вид промаха, стоявшая подсказка и **примета** той
 * ячейки, где кэф оказался на самом деле (`sign.ts`). Ни адреса страницы, ни её
 * текста, ни того, на что человек ставил.
 *
 * Отчёт копится в расширении и уходит с ЛЕНТЫ, а не из расширения: сеть
 * расширение не трогает вовсе, кроме биржи, и ради телеметрии это свойство не
 * меняют. Задержка тут ничего не стоит — чинить подсказку всё равно по
 * накопленному от разных машин.
 */

export const MISS = 'forq:miss';

export interface AimMiss {
  venue: string;
  /** `none` — не нашли ничего; `wrong` — показали не ту ячейку. */
  kind: 'none' | 'wrong';
  /** Подсказка, стоявшая в момент промаха. Пусто — подсказки не было. */
  hint?: string;
  /** Примета ячейки, где кэф оказался на самом деле. Пусто при `none`. */
  path?: string;
}

/** Сколько отчётов держим. Больше не нужно: чинит повторяемость, а не объём. */
export const KEEP = 50;

export function parseMiss(data: unknown): AimMiss | null {
  if (typeof data !== 'object' || data === null) return null;
  const one = data as Record<string, unknown>;
  if (typeof one.venue !== 'string' || one.venue === '') return null;
  if (one.kind !== 'none' && one.kind !== 'wrong') return null;
  const path = typeof one.path === 'string' && one.path !== '' ? one.path : undefined;
  if (one.kind === 'wrong' && path === undefined) return null;
  const hint = typeof one.hint === 'string' && one.hint !== '' ? one.hint : undefined;
  return {
    venue: one.venue,
    kind: one.kind,
    ...(hint === undefined ? {} : { hint }),
    ...(path === undefined ? {} : { path }),
  };
}

export function parseMisses(data: unknown): AimMiss[] {
  if (typeof data !== 'object' || data === null) return [];
  const body = data as Record<string, unknown>;
  if (body.channel !== MISS || !Array.isArray(body.misses)) return [];
  const out: AimMiss[] = [];
  for (const raw of body.misses.slice(0, KEEP)) {
    const one = parseMiss(raw);
    if (one !== null) out.push(one);
  }
  return out;
}

export const missEnvelope = (
  misses: AimMiss[],
): { channel: string; misses: AimMiss[] } => ({ channel: MISS, misses });

/**
 * Отправить накопленное. Молча: отчёт — наш инструмент, а не работа человека.
 *
 * По одному запросу на промах, а не пачкой: их единицы в сутки, а отдельная
 * ручка на пачку — это отдельная проверка размера и отдельный способ уронить
 * ленту чужим списком.
 */
export function report(
  misses: readonly AimMiss[],
  send: (body: AimMiss) => Promise<unknown>,
): void {
  for (const one of misses.slice(0, KEEP)) {
    void Promise.resolve(send(one)).catch(() => {
      // Не доехал — не беда: чинит накопленное от разных машин, а не этот один.
    });
  }
}
